import type { ApiCalendarEntry } from "./types";

export type SseEventType = "created" | "updated" | "deleted";

export interface SseCallback {
  (type: SseEventType, calendarId: string, event: ApiCalendarEntry | { id: string }): void;
}

/**
 * Open an SSE stream for a specific calendar.
 * Returns a cleanup function to close the connection.
 */
export function createCalendarStream(calendarId: string, onSync: SseCallback): () => void {
  let eventSource: EventSource | null = null;
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  let closed = false;

  function connect() {
    if (closed) return;

    eventSource = new EventSource(`/api/calendars/${calendarId}/stream`);

    eventSource.addEventListener("created", (e) => {
      const data = JSON.parse((e as MessageEvent).data);
      onSync("created", calendarId, data);
    });

    eventSource.addEventListener("updated", (e) => {
      const data = JSON.parse((e as MessageEvent).data);
      onSync("updated", calendarId, data);
    });

    eventSource.addEventListener("deleted", (e) => {
      const data = JSON.parse((e as MessageEvent).data);
      onSync("deleted", calendarId, data);
    });

    eventSource.onerror = () => {
      eventSource?.close();
      eventSource = null;
      if (!closed) {
        reconnectTimer = setTimeout(connect, 3000);
      }
    };
  }

  connect();

  return () => {
    closed = true;
    if (reconnectTimer) {
      clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }
    eventSource?.close();
    eventSource = null;
  };
}
